import React, { ReactNode, useEffect, useRef } from "react";
import useWindowDimensions from "../../../../hooks/windowsSize";

interface CommonSplitContainerProps {
  topChild?: ReactNode;
  bottomChild?: ReactNode;
  currentHeight?: number;
  scrollToBottomTrigger?: boolean;
  scrollToTopTrigger?: boolean;
  onScrollPositionChange?: (atBottom: boolean) => void;
  overflowX?: "auto" | "hidden" | "scroll" | "visible";
  hideScrollbar?: boolean;
  className?: string;
  topClassName?: string;
  bottomClassName?: string;
}

const CommonSplitContainer: React.FC<CommonSplitContainerProps> = ({
  topChild,
  bottomChild,
  currentHeight = 0.93,
  scrollToBottomTrigger = false,
  scrollToTopTrigger = false,
  onScrollPositionChange,
  overflowX = "hidden",
  hideScrollbar = true,
  className = "",
  topClassName = "",
  bottomClassName = ""
}) => {
  const { height } = useWindowDimensions();
  const containerRef = useRef<HTMLDivElement>(null);

  const handleScrollToBottom = () => {
    if (scrollToTopTrigger && containerRef.current) {
      containerRef.current.scrollTo({
        top: 0,
        behavior: "smooth"
      });
    }
  };

  const handleScrollToTop = () => {
    if (scrollToBottomTrigger && containerRef.current) {
      containerRef.current.scrollTo({
        top: containerRef.current.scrollHeight,
        behavior: "smooth"
      });
    }
  };

  useEffect(() => {
    handleScrollToTop();
  }, [scrollToBottomTrigger]);

  useEffect(() => {
    handleScrollToBottom();
  }, [scrollToTopTrigger]);

  const handleScroll = () => {
    if (!containerRef.current || !onScrollPositionChange) return;
    const { scrollTop, scrollHeight, clientHeight } = containerRef.current;

    const atBottom = scrollTop + clientHeight >= scrollHeight - 50;
    onScrollPositionChange(atBottom);
  };

  const getOverflowXClass = () => {
    switch (overflowX) {
      case "auto":
        return "tw-overflow-x-auto";
      case "scroll":
        return "tw-overflow-x-scroll";
      case "visible":
        return "tw-overflow-x-visible";
      case "hidden":
      default:
        return "tw-overflow-x-hidden";
    }
  };

  const scrollbarClass = hideScrollbar
    ? "tw-scrollbar-none [&::-webkit-scrollbar]:tw-hidden [-ms-overflow-style:tw-none] [scrollbar-width:tw-none]"
    : "tw-scrollbar-thin tw-scrollbar-thumb-slate-300 tw-scrollbar-track-slate-100 dark:tw-scrollbar-thumb-slate-600 dark:tw-scrollbar-track-slate-800";

  return (
    <div className={className}>
      <div
        ref={containerRef}
        className={`tw-overflow-y-auto ${getOverflowXClass()} ${scrollbarClass} ${topClassName}`}
        style={{
          height: `calc(${height * currentHeight}px - 5rem)` // 5rem = 80px
        }}
        onScroll={handleScroll}
      >
        {topChild}
      </div>
      {bottomChild && <div className={bottomClassName}>{bottomChild}</div>}
    </div>
  );
};

export default CommonSplitContainer;
